"use client";

import React, { useRef, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Worker } from "@/app/components/workercard/WorkerCard";
import { useOrdersContext } from "@/context/OrdersContext";
import {
  WorkerFrequentShopSection,
  WorkerRecentActivitySection,
  type WorkerShopPinDetailBridge,
} from "./WorkerFrequentShopSection";
import { WorkerMarketplaceSection } from "./WorkerMarketplaceSection";
import { WorkerMyTeamSection } from "./WorkerMyTeamSection";
import { Workspace } from "@/lib/api";
import {
  buildHomeHrefAfterWorkspacePick,
  continuePendingBillinGo,
} from "@/lib/billinGoRoutes";
import { persistLastWorkspace } from "@/lib/lastWorkspaceStorage";
import { showWorkspaceSwitchToast } from "@/lib/workspaceSwitchToast";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import { checkServiceHiredInWorkspace } from "@/lib/workspacePickHint";
import { ChevronDown, Plus, Settings } from "lucide-react";
import {
  WorkspaceSelectList,
  WORKSPACE_SELECT_ACTION_ROW_CLASS,
  WORKSPACE_SELECT_AVATAR_SIZE,
  WORKSPACE_SELECT_ICON_SIZE,
} from "@/app/components/WorkspaceSelectList";
import { WorkspaceAvatarDisplay } from "@/app/components/WorkspaceAvatarDisplay";
import {
  DEFAULT_WORKSPACE_AVATAR,
  resolveWorkspaceAvatarFromWorkspace,
  resolveWorkspaceImageDisplayUrl,
} from "@/lib/workspaceAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/DropdownMenu";
import { AeraButton } from "@/app/components/ui/AeraButton";

interface WorkerSidebarProps {
  selectedWorker: Worker | null;
  setSelectedWorker: (
    worker: Worker,
    source?: "my-team" | "marketplace",
  ) => void;
  hiredWorkerIds: string[];
  apiWorkers?: Worker[];
  isLoadingWorkers?: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  width?: number;
  selectedServiceId?: string;
  suppressLoading?: boolean;
  isWorkspaceLoading?: boolean;
  activeTab?: "my-team" | "marketplace";
  workspaces?: Workspace[];
  currentWorkspaceId?: string | null;
  isLoadingWorkspaces?: boolean;
  onOpenWorkspaceModal?: () => void;
  onAddWorkspace?: () => void;
  onWorkspaceUpdated?: () => void;
}

// Persistent scroll position across re-mounts
let sidebarScrollPos = 0;

function WorkerSidebarComponent({
  selectedWorker,
  setSelectedWorker,
  hiredWorkerIds,
  apiWorkers: propApiWorkers,
  isLoadingWorkers: propIsLoadingWorkers,
  searchQuery,
  width,
  selectedServiceId = "",
  suppressLoading = false,
  isWorkspaceLoading = false,
  activeTab,
  workspaces = [],
  currentWorkspaceId = null,
  isLoadingWorkspaces = false,
  onOpenWorkspaceModal,
  onAddWorkspace,
  onWorkspaceUpdated,
}: WorkerSidebarProps) {
  const router = useRouter();
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isSidebarScrolled, setIsSidebarScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pinDetailBridgeRef = useRef<WorkerShopPinDetailBridge | null>(null);
  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId);
  const workspaceForAvatar = currentWorkspace ?? workspaces[0];
  const workspaceAvatar = workspaceForAvatar
    ? resolveWorkspaceAvatarFromWorkspace({
        imageUrl: workspaceForAvatar.imageUrl,
        tags: workspaceForAvatar.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
      })
    : DEFAULT_WORKSPACE_AVATAR;
  const workspaceLabel = isLoadingWorkspaces
    ? "กำลังโหลด..."
    : (currentWorkspace?.name ?? workspaces[0]?.name ?? "Workspace");
  const { loadingOrders, workersFromOrders, loadingOrderWorkers } =
    useOrdersContext();
  const isBillinHired = hiredWorkerIds.includes(BILLIN_SERVICE_ID);

  // Track previous orders count to detect new additions
  const prevOrdersCountRef = useRef<number>(0);

  const syncSidebarScrollShadow = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setIsSidebarScrolled(el.scrollTop > 0);
  };

  // Restore scroll position on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = sidebarScrollPos;
      syncSidebarScrollShadow();
    }
  }, []);

  // Auto-scroll to My Team section when new order is added
  useEffect(() => {
    // Skip if loading or no orders
    if (loadingOrders || loadingOrderWorkers) return;

    const currentCount = workersFromOrders.length;
    const prevCount = prevOrdersCountRef.current;

    // If new order was added (count increased), scroll to top to show My Team
    if (currentCount > prevCount && prevCount > 0) {
      // Scroll to top of sidebar to show My Team section with new service
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        setIsSidebarScrolled(false);
      }
    }

    // Update ref with current count
    prevOrdersCountRef.current = currentCount;
  }, [workersFromOrders.length, loadingOrders, loadingOrderWorkers]);

  // Helper to handle worker selection with scroll preservation
  const handleWorkerClick = (
    worker: Worker,
    source: "my-team" | "marketplace",
  ) => {
    if (scrollContainerRef.current) {
      sidebarScrollPos = scrollContainerRef.current.scrollTop;
    }
    setSelectedWorker(worker, source);
  };

  return (
    <aside
      style={{ width: width ? `${width}px` : undefined }}
      className={`h-full min-h-0 flex flex-col overflow-hidden shrink-0 transition-all duration-300 ${!width ? "w-72" : ""} max-md:w-full!`}
    >
      <div
        className={`shrink-0 z-10 flex flex-col gap-1 items-start justify-start bg-aera-white px-4 pt-4 pb-3 transition-shadow duration-200 ${
          isSidebarScrolled
            ? "shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08)]"
            : ""
        }`}
      >
        <DropdownMenu
          open={isWorkspaceDropdownOpen}
          onOpenChange={setIsWorkspaceDropdownOpen}
        >
          <DropdownMenuTrigger asChild>
            <AeraButton
              type="button"
              variant="transparent"
              textSize="lg"
              fontWeight="bold"
              paddingSize="xxs"
              aria-label={`เลือก Workspace: ${workspaceLabel}`}
              aria-expanded={isWorkspaceDropdownOpen}
              title={workspaceLabel}
              icon={
                <WorkspaceAvatarDisplay
                  avatar={workspaceAvatar}
                  size={WORKSPACE_SELECT_AVATAR_SIZE}
                  iconSize={WORKSPACE_SELECT_ICON_SIZE}
                  className="shrink-0"
                />
              }
              iconRight={
                <ChevronDown
                  size={14}
                  className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                    isWorkspaceDropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              }
            >
              <span className="tracking-tight truncate max-w-[180px] text-aera-charcoal">
                {workspaceLabel}
              </span>
            </AeraButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            collisionPadding={12}
            className="flex min-w-[240px] max-w-[min(280px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-aera-lightgray/80 p-1.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] animate-in fade-in zoom-in-95 duration-200 max-h-[min(var(--radix-dropdown-menu-content-available-height),calc(100dvh-5rem))]"
          >
            <p className="px-2.5 pb-0.5 pt-1 text-xs font-bold uppercase tracking-[0.05em] text-aera-gray">
              ร้าน / พื้นที่ทำงาน
            </p>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <WorkspaceSelectList
                workspaces={workspaces}
                selectedWorkspaceId={currentWorkspaceId}
                isLoading={isLoadingWorkspaces}
                onSelect={(workspaceId) => {
                  setIsWorkspaceDropdownOpen(false);
                  if (workspaceId !== currentWorkspaceId) {
                    const picked = workspaces.find((w) => w.id === workspaceId);
                    showWorkspaceSwitchToast(picked?.name);
                  }
                  persistLastWorkspace(workspaceId);
                  if (
                    continuePendingBillinGo(workspaceId, (href) => {
                      router.replace(href, {
                        scroll: false,
                      });
                    })
                  ) {
                    return;
                  }
                  const sid = selectedServiceId?.trim();
                  if (sid) {
                    void (async () => {
                      const isHired = await checkServiceHiredInWorkspace(
                        workspaceId,
                        sid,
                      );
                      router.replace(
                        buildHomeHrefAfterWorkspacePick(workspaceId, sid, {
                          isHired,
                        }),
                        { scroll: false },
                      );
                    })();
                    return;
                  }
                  router.push(`/?workspaceId=${workspaceId}`, {
                    scroll: false,
                  });
                }}
              />
            </div>
            {(onAddWorkspace || onOpenWorkspaceModal) && (
              <>
                <DropdownMenuSeparator className="my-1" />
                <div className="shrink-0 flex flex-col gap-0.5">
                  {onOpenWorkspaceModal && (
                    <DropdownMenuItem
                      className={WORKSPACE_SELECT_ACTION_ROW_CLASS}
                      onSelect={onOpenWorkspaceModal}
                    >
                      <Settings size={16} strokeWidth={2.5} className="shrink-0" />
                      <span>ตั้งค่าร้านนี้</span>
                    </DropdownMenuItem>
                  )}
                  {onAddWorkspace && (
                    <DropdownMenuItem
                      className={WORKSPACE_SELECT_ACTION_ROW_CLASS}
                      onSelect={onAddWorkspace}
                    >
                      <Plus size={16} strokeWidth={2.5} className="shrink-0" />
                      <span>เพิ่มร้าน / พื้นที่ใหม่</span>
                    </DropdownMenuItem>
                  )}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="ps-1.5 text-xs text-aera-gray">
          พื้นที่ร้าน · ข้อมูลที่ Bill-Lin และทีม AI ใช้ทำงาน
        </span>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        onScroll={syncSidebarScrollShadow}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col gap-6 px-4 py-4"
      >
        {isBillinHired ? (
          <WorkerFrequentShopSection
            workspace={currentWorkspace ?? null}
            onWorkspaceUpdated={onWorkspaceUpdated}
            pinDetailBridgeRef={pinDetailBridgeRef}
            isWorkspaceLoading={isLoadingWorkspaces}
          />
        ) : null}

        {/* My Team Section - Display workers from orders API */}
        <WorkerMyTeamSection
          activeTab={activeTab}
          selectedWorker={selectedWorker}
          handleWorkerClick={handleWorkerClick}
          searchQuery={searchQuery}
          suppressLoading={suppressLoading}
          forceLoading={isWorkspaceLoading}
        />

        {/* Marketplace Section */}
        <WorkerMarketplaceSection
          activeTab={activeTab}
          selectedWorker={selectedWorker}
          handleWorkerClick={handleWorkerClick}
          searchQuery={searchQuery}
          hiredWorkerIds={hiredWorkerIds}
          suppressLoading={suppressLoading}
          forceLoading={isWorkspaceLoading}
          apiWorkers={propApiWorkers}
          isLoadingWorkers={isWorkspaceLoading ? true : propIsLoadingWorkers}
        />

        {isBillinHired ? (
          <WorkerRecentActivitySection workspace={currentWorkspace ?? null} />
        ) : null}
      </div>
    </aside>
  );
}

// Export memoized version to prevent unnecessary re-renders
export default React.memo(WorkerSidebarComponent);
